import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, DollarSign, CreditCard, TrendingUp, CheckCircle2, AlertTriangle,
  XCircle, Loader2, ArrowRight, ArrowLeft, Phone, Mail, Sparkles,
  Crown, Lock, Target, Building2, Clock, Camera, User, Calendar as CalendarIcon,
  Car,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import PayPalButton from "../components/PayPalButton";
import BrokerScheduleModal from "../components/BrokerScheduleModal";
import BuyingPowerGauge from "../components/BuyingPowerGauge";
import ClosingCostEstimator from "../components/ClosingCostEstimator";
import CarBuyingPowerFlow from "../components/CarBuyingPowerFlow";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export default function BuyingPowerPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);  // null | "property" | "car"
  const [step, setStep] = useState(1);
  const [ocrScanning, setOcrScanning] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [unlockingReport, setUnlockingReport] = useState(false);
  const [proReport, setProReport] = useState(null);
  const [listing, setListing] = useState({ url: "", price: "", title: "", image: "" });
  const [manual, setManual] = useState(false);
  const [fin, setFin] = useState({
    annual_income: "", monthly_debts: "", down_payment: "",
    employment_status: "employed", dependents: 0,
  });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [teaserProperties, setTeaserProperties] = useState([]);
  const [dpMode, setDpMode] = useState("custom"); // "5", "10", "custom"
  const [promo, setPromo] = useState({ code: "", applying: false, result: null, error: "" });
  const [showBrokerModal, setShowBrokerModal] = useState(false);

  // Auto-scroll: when step changes, bring the next step into view
  useEffect(() => {
    const map = { 1: "step-1-listing", 2: "step-2-income", 3: "step-3-debts", 4: "step-4-results" };
    const el = document.querySelector(`[data-testid='${map[step]}']`);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [step]);

  // Auto-scroll: when paywall opens, bring it into view
  useEffect(() => {
    if (showPayment || proReport) {
      const el = document.querySelector("[data-testid='paywall-section'], [data-testid='pro-report']");
      if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [showPayment, proReport]);

  const applyPromo = async () => {
    if (!promo.code.trim()) return;
    setPromo((p) => ({ ...p, applying: true, error: "", result: null }));
    try {
      const res = await axios.post(`${API}/api/promo/validate`, {
        code: promo.code.trim(),
        base_price: 29.99,
      });
      setPromo((p) => ({ ...p, applying: false, result: res.data, error: "" }));
      if (res.data.is_free) {
        toast.success("Promo applied — your report is 100% free!");
      } else {
        toast.success(`Promo applied — $${res.data.discount_amount.toFixed(2)} off`);
      }
    } catch (e) {
      setPromo((p) => ({ ...p, applying: false, result: null, error: e?.response?.data?.detail || "Invalid promo code" }));
    }
  };

  const unlockWithPromo = async () => {
    if (!contact.name?.trim() || !contact.email) {
      return toast.error("Please enter your name and email first");
    }
    // Save lead first
    try {
      await axios.post(`${API}/api/buying-power/lead`, {
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_url: listing.url, listing_price: parseFloat(listing.price) || null,
        calculated_low: result.low, calculated_high: result.high, match_status: result.match_status,
      });
    } catch { /* non-fatal */ }
    // Call pro-report with FREE + promo_code
    setUnlockingReport(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/pro-report`, {
        transaction_id: "FREE",
        promo_code: promo.code.trim(),
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_url: listing.url, listing_price: parseFloat(listing.price) || null,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        employment_status: fin.employment_status,
        dependents: parseInt(fin.dependents) || 0,
      });
      setProReport(res.data);
      // Remember that this email paid for a Pro report so the full
      // qualification form skips the second paywall.
      try {
        sessionStorage.setItem("buyingPowerPaid", JSON.stringify({
          email: contact.email, transaction_id: "FREE",
        }));
      } catch { /* ignore */ }
      toast.success("Premium report unlocked with promo!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promo unlock failed");
    } finally {
      setUnlockingReport(false);
    }
  };

  // Compute down payment from % of listing price
  const applyDpMode = (mode) => {
    setDpMode(mode);
    const price = parseFloat(listing.price);
    if (mode === "custom" || !price) return;
    const pct = mode === "5" ? 0.05 : 0.10;
    setFin((f) => ({ ...f, down_payment: Math.round(price * pct).toString() }));
  };

  const importFromScreenshot = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please pick an image file.");
    if (file.size > 6 * 1024 * 1024) return toast.error("Image too large (max 6MB).");
    setOcrScanning(true);
    setScrapeError(null);
    // Preserve the uploaded screenshot as the hero preview immediately
    const previewUrl = URL.createObjectURL(file);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/api/buying-power/scrape-image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.price) {
        setListing((l) => ({
          ...l,
          price: res.data.price,
          title: res.data.title || l.title,
          image: previewUrl,
        }));
        setManual(true);
        toast.success(`Detected ${fmt(res.data.price)} from your screenshot.`);
      } else {
        setListing((l) => ({ ...l, image: previewUrl }));
        setScrapeError("Couldn't read a price from that image. Please enter it manually below.");
        setManual(true);
      }
    } catch (e) {
      setScrapeError(e?.response?.data?.detail || "Screenshot scan failed. Enter price manually below.");
      setManual(true);
    } finally {
      setOcrScanning(false);
    }
  };

  const calculate = async () => {
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/calculate`, {
        listing_price: parseFloat(listing.price) || null,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        employment_status: fin.employment_status,
        dependents: parseInt(fin.dependents) || 0,
      });
      setResult(res.data);
      setStep(4);
      // Teaser: if not_yet, fetch 3 homes within reach so the user doesn't bounce
      if (res.data.match_status === "not_yet") {
        try {
          const sim = await axios.post(`${API}/api/buying-power/similar-properties`, {
            min_price: Math.max(0, res.data.low * 0.8),
            max_price: res.data.high,
            limit: 3,
          });
          setTeaserProperties(sim.data.properties || []);
        } catch {
          setTeaserProperties([]);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const startCheckout = async () => {
    if (!contact.name?.trim()) return toast.error("Please enter your name");
    if (!contact.email) return toast.error("Email is required to unlock your report");
    setGateSubmitting(true);
    try {
      // Save the lead as unpaid first (so we have a record if they abandon checkout)
      await axios.post(`${API}/api/buying-power/lead`, {
        name: contact.name, email: contact.email, phone: contact.phone,
        listing_url: listing.url, listing_price: parseFloat(listing.price) || null,
        calculated_low: result.low, calculated_high: result.high, match_status: result.match_status,
      });
      setShowPayment(true);
    } catch {
      toast.error("Please enter a valid email to continue");
    } finally {
      setGateSubmitting(false);
    }
  };

  const fetchProReport = async (transaction_id) => {
    setUnlockingReport(true);
    try {
      const res = await axios.post(`${API}/api/buying-power/pro-report`, {
        transaction_id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        listing_url: listing.url,
        listing_price: parseFloat(listing.price) || null,
        annual_income: parseFloat(fin.annual_income),
        monthly_debts: parseFloat(fin.monthly_debts) || 0,
        down_payment: parseFloat(fin.down_payment) || 0,
        employment_status: fin.employment_status,
        dependents: parseInt(fin.dependents) || 0,
      });
      setProReport(res.data);
      try {
        sessionStorage.setItem("buyingPowerPaid", JSON.stringify({
          email: contact.email, transaction_id,
        }));
      } catch { /* ignore */ }
      toast.success("Premium report unlocked!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report. Please contact support.");
    } finally {
      setUnlockingReport(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    const txId = paymentData?.transaction_id || paymentData?.id || "UNKNOWN";
    await fetchProReport(txId);
  };

  const startFullApp = () => {
    sessionStorage.setItem("buyingPowerPrefill", JSON.stringify({
      gross_monthly_salary: parseFloat(fin.annual_income) / 12,
      credit_card_payment: parseFloat(fin.monthly_debts) || 0,
      email: contact.email, phone: contact.phone, name: contact.name,
      // Flag the full qualification form that this user already paid for Pro
      // via the Buying Power calculator so we skip the second paywall.
      bp_paid: true,
    }));
    navigate("/qualify");
  };

  const totalSteps = 4;
  const pct = (step / totalSteps) * 100;

  // --- Mode selector screen (shown before any wizard) ---
  if (!mode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fffe] via-white to-[#f0fafa] pt-24 pb-16 px-4 flex items-center">
        <div className="max-w-4xl mx-auto w-full">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#52BFB8]/10 text-[#3E9690] text-xs font-bold tracking-wider uppercase mb-4">
              <Sparkles className="w-3.5 h-3.5" /> BETA · Free check
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2D3748] mb-3 font-cabinet">
              What are you looking to buy?
            </h1>
            <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto">
              Pick one — we'll scan the listing, run the Bahamian lender math, and tell you where you stand.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-7">
            <button
              onClick={() => setMode("property")}
              data-testid="mode-select-property"
              className="group relative overflow-hidden bg-white rounded-3xl border-2 border-gray-100 hover:border-[#52BFB8] shadow-lg hover:shadow-2xl transition-all duration-300 p-8 sm:p-10 text-left"
            >
              <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full bg-gradient-to-br from-[#52BFB8]/10 to-transparent group-hover:scale-110 transition-transform" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg">
                  <Home className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#2D3748] mb-2 font-cabinet">Property</h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Home, condo, land or investment property. We'll check your mortgage range across all six Bahamian lenders.
                </p>
                <div className="inline-flex items-center text-sm font-semibold text-[#3E9690] group-hover:translate-x-1 transition-transform">
                  Check property buying power
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode("car")}
              data-testid="mode-select-car"
              className="group relative overflow-hidden bg-white rounded-3xl border-2 border-gray-100 hover:border-[#52BFB8] shadow-lg hover:shadow-2xl transition-all duration-300 p-8 sm:p-10 text-left"
            >
              <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full bg-gradient-to-br from-[#52BFB8]/10 to-transparent group-hover:scale-110 transition-transform" />
              <div className="absolute top-5 right-5 bg-[#3E9690] text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full">
                New
              </div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3E9690] to-[#2A7873] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg">
                  <Car className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#2D3748] mb-2 font-cabinet">Car</h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  New or used, gas / hybrid / electric. We factor Bahamas customs duty for imports and match you to the right auto lender.
                </p>
                <div className="inline-flex items-center text-sm font-semibold text-[#3E9690] group-hover:translate-x-1 transition-transform">
                  Check car buying power
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            Free range + qualify check · Pro Report ($29.99) unlocks per-bank rates, terms &amp; emailed report
          </p>
        </div>
      </div>
    );
  }

  // --- Car flow (fully encapsulated) ---
  if (mode === "car") {
    return <CarBuyingPowerFlow onBack={() => setMode(null)} />;
  }

  // --- Property flow (existing wizard below) ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fffe] via-white to-[#f0fafa] pt-24 pb-16 px-4">
      {/* After premium unlock we switch to a wider landscape layout on
          desktop (lg+) while keeping the beloved stacked portrait on mobile. */}
      <div className={`${step === 4 && proReport ? "max-w-2xl lg:max-w-6xl" : "max-w-2xl"} mx-auto`}>
        <button
          onClick={() => setMode(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#3E9690] mb-4 transition-colors"
          data-testid="property-back-to-mode"
        >
          <ArrowLeft className="w-4 h-4" /> Back to choice
        </button>
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#52BFB8] to-[#3E9690] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-10" data-testid="buying-power-card">
          {/* Step 1: Listing URL */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1-listing">
              <div className="text-center mb-2">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center mb-4">
                  <Home className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-[#2D3748] mb-2 font-cabinet">Check Your Buying Power</h1>
                <p className="text-gray-500 text-sm">Snap a screenshot of any listing — we'll read the price and do the math.</p>
              </div>

              {scrapeError && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-sm text-amber-800" data-testid="scrape-error-banner">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{scrapeError}</span>
                </div>
              )}

              {!manual && (
                <>
                  <label
                    htmlFor="listing-screenshot"
                    className={`flex flex-col items-center justify-center gap-2 w-full py-10 rounded-xl border-2 border-dashed cursor-pointer transition ${ocrScanning ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-300 hover:border-[#52BFB8] hover:bg-[#52BFB8]/5"}`}
                    data-testid="screenshot-upload-label"
                  >
                    {ocrScanning ? (
                      <>
                        <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
                        <span className="text-sm text-[#3E9690] font-medium">Reading screenshot...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-[#52BFB8]" />
                        <span className="text-base text-[#2D3748] font-semibold">Upload a screenshot of the listing</span>
                        <span className="text-xs text-gray-400">PNG · JPG · WEBP · up to 6MB</span>
                      </>
                    )}
                    <input
                      id="listing-screenshot"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => importFromScreenshot(e.target.files?.[0])}
                      data-testid="screenshot-upload-input"
                    />
                  </label>

                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  <button onClick={() => setManual(true)} className="text-sm text-[#52BFB8] hover:underline block mx-auto" data-testid="skip-manual-btn">
                    Enter price manually
                  </button>
                </>
              )}

              {manual && (
                <div className="space-y-4">
                  {listing.image && (
                    <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden" data-testid="listing-preview-card">
                      <div className="relative w-full aspect-[16/9] bg-gray-100">
                        <img
                          src={listing.image}
                          alt={listing.title || "Listing"}
                          className="w-full h-full object-cover"
                          data-testid="listing-hero-image"
                        />
                        {listing.title && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                            <p className="text-white font-semibold text-sm truncate" data-testid="listing-hero-title">{listing.title}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!listing.price && !ocrScanning && (
                    <label
                      htmlFor="listing-screenshot-manual"
                      className="flex items-center justify-center gap-2 w-full h-11 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#52BFB8] hover:bg-[#52BFB8]/5 cursor-pointer transition"
                    >
                      <Camera className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 font-medium">Upload a screenshot instead</span>
                      <input
                        id="listing-screenshot-manual"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => importFromScreenshot(e.target.files?.[0])}
                      />
                    </label>
                  )}
                  {ocrScanning && (
                    <div className="flex items-center justify-center gap-2 h-11 rounded-xl bg-[#52BFB8]/5 border-2 border-[#52BFB8]/40">
                      <Loader2 className="w-4 h-4 animate-spin text-[#52BFB8]" />
                      <span className="text-sm text-[#3E9690] font-medium">Reading screenshot...</span>
                    </div>
                  )}
                  <label className="text-sm font-medium text-[#4A4A4A]">Property price</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={listing.price}
                      onChange={(e) => setListing({ ...listing, price: e.target.value })}
                      placeholder="300000"
                      className="pl-10 h-12"
                      data-testid="manual-price-input"
                    />
                  </div>
                  <Button onClick={() => listing.price ? setStep(2) : toast.error("Enter a price")} className="btn-primary w-full h-12">
                    Continue<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Income */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2-income">
              {listing.price && (
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                  {listing.image && <img src={listing.image} alt="" className="w-12 h-12 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 truncate">{listing.title || "Property"}</p>
                    <p className="font-bold text-[#2D3748]">{fmt(listing.price)}</p>
                  </div>
                </div>
              )}
              <div className="text-center">
                <TrendingUp className="w-10 h-10 mx-auto text-[#52BFB8] mb-2" />
                <h2 className="text-2xl font-bold text-[#2D3748]">Your income</h2>
                <p className="text-sm text-gray-500">Total annual income before tax</p>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="number" value={fin.annual_income} onChange={(e) => setFin({ ...fin, annual_income: e.target.value })} placeholder="e.g. 85000" className="pl-10 h-12" data-testid="annual-income-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setFin({ ...fin, employment_status: "employed" })} className={`p-3 rounded-xl border-2 transition ${fin.employment_status === "employed" ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200"}`} data-testid="employed-btn">Employed</button>
                <button onClick={() => setFin({ ...fin, employment_status: "self_employed" })} className={`p-3 rounded-xl border-2 transition ${fin.employment_status === "self_employed" ? "border-[#52BFB8] bg-[#52BFB8]/5" : "border-gray-200"}`} data-testid="self-employed-btn">Self-Employed</button>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={() => fin.annual_income ? setStep(3) : toast.error("Enter income")} className="btn-primary flex-1" data-testid="income-next-btn">Next<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {/* Step 3: Liabilities */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3-debts">
              <div className="text-center">
                <CreditCard className="w-10 h-10 mx-auto text-[#52BFB8] mb-2" />
                <h2 className="text-2xl font-bold text-[#2D3748]">Debts & down payment</h2>
                <p className="text-sm text-gray-500">Total monthly minimums across all liabilities</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A4A4A] block mb-1">Total monthly debt payments</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="number" value={fin.monthly_debts} onChange={(e) => setFin({ ...fin, monthly_debts: e.target.value })} placeholder="e.g. 500" className="pl-10 h-12" data-testid="monthly-debts-input" />
                </div>
                <p className="text-xs text-gray-400 mt-1">Credit cards, personal loans, other liabilities</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A4A4A] block mb-2">Down payment</label>
                {listing.price && (
                  <div className="grid grid-cols-3 gap-2 mb-3" data-testid="dp-mode-selector">
                    <button
                      type="button"
                      onClick={() => applyDpMode("5")}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition ${dpMode === "5" ? "border-[#52BFB8] bg-[#52BFB8]/5 text-[#2D3748]" : "border-gray-200 text-gray-600"}`}
                      data-testid="dp-mode-5"
                    >
                      <div className="font-bold">5%</div>
                      <div className="text-xs text-gray-500 mt-0.5">{fmt(parseFloat(listing.price) * 0.05)}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => applyDpMode("10")}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition ${dpMode === "10" ? "border-[#52BFB8] bg-[#52BFB8]/5 text-[#2D3748]" : "border-gray-200 text-gray-600"}`}
                      data-testid="dp-mode-10"
                    >
                      <div className="font-bold">10%</div>
                      <div className="text-xs text-gray-500 mt-0.5">{fmt(parseFloat(listing.price) * 0.10)}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => applyDpMode("custom")}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition ${dpMode === "custom" ? "border-[#52BFB8] bg-[#52BFB8]/5 text-[#2D3748]" : "border-gray-200 text-gray-600"}`}
                      data-testid="dp-mode-custom"
                    >
                      <div className="font-bold">Custom</div>
                      <div className="text-xs text-gray-500 mt-0.5">Enter amount</div>
                    </button>
                  </div>
                )}
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    value={fin.down_payment}
                    onChange={(e) => { setFin({ ...fin, down_payment: e.target.value }); setDpMode("custom"); }}
                    placeholder="e.g. 15000"
                    className="pl-10 h-12"
                    data-testid="down-payment-input"
                  />
                </div>
                {listing.price && fin.down_payment && (
                  <p className="text-xs text-[#3E9690] mt-1" data-testid="dp-percent-hint">
                    {((parseFloat(fin.down_payment) / parseFloat(listing.price)) * 100).toFixed(1)}% of {fmt(listing.price)}
                    {parseFloat(fin.down_payment) < parseFloat(listing.price) * 0.05 && " · below 5% minimum"}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A4A4A] block mb-1">Dependents (optional)</label>
                <Input type="number" value={fin.dependents} onChange={(e) => setFin({ ...fin, dependents: e.target.value })} placeholder="0" className="h-12" min="0" data-testid="dependents-input" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={calculate} disabled={calculating || !fin.down_payment} className="btn-primary flex-1" data-testid="calculate-btn">
                  {calculating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating...</> : <>See My Range<Sparkles className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && result && (
            <div className="space-y-6" data-testid="step-4-results">
              <MatchBadge status={result.match_status} />

              {/* Premium gauge visualization */}
              <div className="rounded-2xl bg-gradient-to-b from-white to-[#F8FDFD] border border-gray-100 p-5 sm:p-6 shadow-sm">
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-2 text-center font-semibold">
                  Your buying power range
                </p>
                <BuyingPowerGauge
                  low={result.low}
                  high={result.high}
                  listingPrice={parseFloat(listing.price) || null}
                  matchStatus={result.match_status}
                  size={320}
                />
                {/* Screen-reader + test-only range */}
                <p
                  className="sr-only"
                  data-testid="buying-power-range"
                >
                  {fmt(result.low)} – {fmt(result.high)}
                </p>
              </div>

              <p className="text-center text-sm text-gray-600 bg-gray-50 rounded-xl p-4">{result.match_message}</p>

              {result.match_status === "not_yet" && teaserProperties.length > 0 && (
                <div data-testid="teaser-properties-block">
                  <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3 text-center">
                    Homes within reach right now
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {teaserProperties.map((p, i) => {
                      const Card = p.href ? "a" : "div";
                      return (
                        <Card
                          key={p.id || i}
                          {...(p.href ? { href: p.href, target: "_blank", rel: "noreferrer" } : {})}
                          className="rounded-xl overflow-hidden border border-gray-100 bg-white hover:shadow-md hover:border-[#52BFB8]/40 transition group block"
                          data-testid={`teaser-property-${i}`}
                        >
                          <div className="relative w-full aspect-[4/3] bg-gray-100 overflow-hidden">
                            <PropertyThumb
                              src={p.image}
                              alt={p.title}
                              className="group-hover:scale-105 transition duration-300"
                              testId={`teaser-img-${i}`}
                            />
                            <div className="absolute top-2 left-2 bg-white/95 backdrop-blur rounded-full px-2 py-0.5 text-xs font-bold text-[#2D3748] shadow-sm">
                              {fmt(p.price)}
                            </div>
                            {p.source === "keysbahamas.com" && (
                              <div className="absolute top-2 right-2 bg-black/60 text-white rounded-full px-2 py-0.5 text-[10px] font-medium">
                                Live
                              </div>
                            )}
                          </div>
                          <div className="p-2.5">
                            <p className="font-semibold text-xs text-[#2D3748] line-clamp-1">{p.title}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{p.location}</p>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Closing Cost & Out-of-Pocket Estimator — only shown after Pro unlock.
                  While locked, this feature is listed inside the single $29.99
                  paywall card below so users never see two separate prices. */}
              {proReport && (() => {
                const bpPrice = parseFloat(listing.price) || result.high || 350_000;
                const bpDpAmt = parseFloat(fin.down_payment) || 0;
                const bpDpPct = bpPrice > 0 ? Math.max(0, Math.min(1, bpDpAmt / bpPrice)) : 0.10;
                return (
                  <ClosingCostEstimator
                    mode="premium"
                    email={contact.email}
                    initialInputs={{
                      price: bpPrice,
                      down_pct: bpDpPct || 0.10,
                      is_bahamian: true,
                      first_time: false,
                      age_band: "under_40",
                    }}
                  />
                );
              })()}

              {!proReport ? (
                <div className="bg-gradient-to-br from-[#52BFB8]/8 to-transparent border border-[#52BFB8]/30 rounded-2xl p-5" data-testid="paywall-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-[#52BFB8]" />
                    <p className="font-semibold text-[#2D3748]">
                      {result.match_status === "not_yet"
                        ? "Unlock your Personalized Qualifying Roadmap"
                        : "Unlock your Premium Buying Report"}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    {result.match_status === "not_yet"
                      ? "See exactly why you fell short, the specific gap in dollars, and a step-by-step plan to close it — tailored to your numbers."
                      : "See your lender-by-lender playbook, exact rate estimates, down-payment status, and next steps to secure pre-approval."}
                  </p>

                  {/* What's included — single paywall, every Pro feature */}
                  <ul className="text-sm text-[#2D3748] space-y-1.5 mb-4" data-testid="pro-feature-list">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#3E9690] flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Total Out-of-Pocket Estimator</strong> — down payment,
                        VAT, legal, bank &amp; insurance, itemized to the dollar
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#3E9690] flex-shrink-0 mt-0.5" />
                      <span>Lender-by-lender match playbook with rate estimates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#3E9690] flex-shrink-0 mt-0.5" />
                      <span>First-time Bahamian exemption automatically applied</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#3E9690] flex-shrink-0 mt-0.5" />
                      <span>Downloadable branded PDF summary &amp; broker call-back</span>
                    </li>
                  </ul>

                  <div className="flex items-baseline gap-2 mb-4">
                    {promo.result && !promo.result.is_free ? (
                      <>
                        <p className="text-3xl font-bold text-[#2D3748]">${promo.result.final_price.toFixed(2)}</p>
                        <p className="text-lg text-gray-400 line-through">$29.99</p>
                        <p className="text-xs text-[#3E9690] font-semibold">Promo applied</p>
                      </>
                    ) : promo.result && promo.result.is_free ? (
                      <>
                        <p className="text-3xl font-bold text-[#3E9690]">FREE</p>
                        <p className="text-lg text-gray-400 line-through">$29.99</p>
                        <p className="text-xs text-[#3E9690] font-semibold">Promo applied</p>
                      </>
                    ) : (
                      <>
                        <p className="text-3xl font-bold text-[#2D3748]">$29<span className="text-lg">.99</span></p>
                        <p className="text-xs text-gray-400">One-time · Instant access</p>
                      </>
                    )}
                  </div>

                  {!showPayment ? (
                    <>
                      <div className="space-y-3 mb-4">
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input type="text" placeholder="Full name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="pl-10" data-testid="gate-name-input" />
                        </div>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input type="email" placeholder="Email address" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="pl-10" data-testid="gate-email-input" />
                        </div>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input type="tel" placeholder="Phone (optional)" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="pl-10" data-testid="gate-phone-input" />
                        </div>
                      </div>

                      {/* Promo code row */}
                      <div className="mb-4">
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="Promo code (optional)"
                            value={promo.code}
                            onChange={(e) => setPromo((p) => ({ ...p, code: e.target.value.toUpperCase(), error: "", result: null }))}
                            className="flex-1 h-10"
                            data-testid="promo-code-input"
                          />
                          <Button
                            variant="outline"
                            onClick={applyPromo}
                            disabled={promo.applying || !promo.code.trim()}
                            className="h-10 whitespace-nowrap"
                            data-testid="promo-apply-btn"
                          >
                            {promo.applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                          </Button>
                        </div>
                        {promo.error && <p className="text-xs text-rose-500 mt-1" data-testid="promo-error">{promo.error}</p>}
                        {promo.result && (
                          <p className="text-xs text-[#3E9690] mt-1" data-testid="promo-success">
                            ✓ {promo.result.description || `$${promo.result.discount_amount.toFixed(2)} off applied`}
                          </p>
                        )}
                      </div>

                      {promo.result?.is_free ? (
                        <Button onClick={unlockWithPromo} disabled={unlockingReport} className="btn-primary w-full" data-testid="unlock-free-btn">
                          {unlockingReport ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Unlocking...</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" />Unlock My Free Report</>
                          )}
                        </Button>
                      ) : (
                        <Button onClick={startCheckout} disabled={gateSubmitting} className="btn-primary w-full" data-testid="unlock-btn">
                          {gateSubmitting ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing checkout...</>
                          ) : (
                            <><Lock className="w-4 h-4 mr-2" />
                              {result.match_status === "not_yet" ? "Get My Personalized Report" : "Get My Detailed Report"}
                            </>
                          )}
                        </Button>
                      )}

                      <p className="text-xs text-gray-400 text-center mt-3">
                        Secure payment via PayPal · No subscription · Support:{" "}
                        <a href="mailto:support@financify-caribbean.com" className="text-[#3E9690] hover:underline" data-testid="support-email-link">
                          support@financify-caribbean.com
                        </a>
                      </p>
                    </>
                  ) : (
                    <div className="space-y-3" data-testid="paypal-section">
                      {unlockingReport ? (
                        <div className="flex items-center justify-center gap-2 py-6 text-gray-500">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Unlocking your premium report...
                        </div>
                      ) : (
                        <PayPalButton
                          amount={29.99}
                          onSuccess={handlePaymentSuccess}
                          onError={() => toast.error("Payment failed. Please try again.")}
                        />
                      )}
                      <button
                        onClick={() => setShowPayment(false)}
                        className="w-full text-sm text-gray-500 hover:text-gray-700"
                        data-testid="cancel-payment-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <ProReportView
                  report={proReport}
                  result={result}
                  contact={contact}
                  onStartApp={startFullApp}
                  onBookBroker={() => setShowBrokerModal(true)}
                />
              )}
              <p className="text-xs text-gray-400 text-center">{result.disclaimer}</p>
            </div>
          )}
        </div>
      </div>

      {/* Broker Call-back Scheduling Modal */}
      <BrokerScheduleModal
        open={showBrokerModal}
        onClose={() => setShowBrokerModal(false)}
        defaults={{
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          listing_url: listing.url,
          listing_price: parseFloat(listing.price) || null,
        }}
      />
    </div>
  );
}

function PropertyThumb({ src, alt, className = "", testId }) {
  const [failed, setFailed] = useState(!src);
  if (failed || !src) {
    return (
      <div
        className={`relative bg-gradient-to-br from-[#52BFB8]/15 via-[#52BFB8]/5 to-[#F0A500]/10 flex items-center justify-center ${className}`}
        data-testid={testId ? `${testId}-placeholder` : undefined}
      >
        <Home className="w-10 h-10 text-[#52BFB8]/50" strokeWidth={1.4} />
        <div className="absolute inset-0 pointer-events-none"
             style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 10px, rgba(82,191,184,0.04) 10px 11px)" }} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover w-full h-full ${className}`}
      loading="lazy"
      onError={() => setFailed(true)}
      data-testid={testId}
    />
  );
}


function MatchBadge({ status }) {
  const config = {
    strong: { Icon: CheckCircle2, color: "emerald", label: "Strong Match" },
    possible: { Icon: AlertTriangle, color: "amber", label: "Possible With Conditions" },
    not_yet: { Icon: XCircle, color: "rose", label: "Not Yet — Here's Why" },
  }[status] || { Icon: CheckCircle2, color: "emerald", label: "Estimate" };
  const { Icon, color, label } = config;
  return (
    <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-${color}-50 text-${color}-700 text-sm font-semibold w-fit mx-auto border border-${color}-200`} data-testid={`match-badge-${status}`}>
      <Icon className="w-4 h-4" />
      {label}
    </div>
  );
}

function ProReportView({ report, onStartApp, onBookBroker, result, contact }) {
  const isNotYet = report.match_status === "not_yet";
  const matchedBanks = (report.lender_playbook && report.lender_playbook.length)
    || (result?.qualifying_banks?.length)
    || 0;
  const firstName = (contact?.name || "").trim().split(" ")[0];

  // Email-me-this-report state
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const sendProEmail = async () => {
    if (!contact?.email) {
      toast.error("No email on file for this report.");
      return;
    }
    setEmailing(true);
    try {
      await axios.post(`${API}/api/buying-power/email-pro-report`, {
        email: contact.email,
        name: contact.name,
        report,
      });
      setEmailed(true);
      toast.success(`Report sent to ${contact.email}`);
      setTimeout(() => setEmailed(false), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <>
      {/* ======================= HERO BANNER ======================= */}
      <div
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F1724] via-[#152034] to-[#0C1120] text-white p-5 sm:p-6 lg:p-8 mb-5 shadow-lg"
        data-testid="pro-hero-banner"
      >
        {/* Decorative glow */}
        <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#52BFB8]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 bg-amber-400/15 text-amber-300 border border-amber-400/30 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
              <Crown className="w-3 h-3" />
              Pro Report Unlocked
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-cabinet leading-tight">
              {firstName ? <>Great news, <span className="text-[#52BFB8]">{firstName}</span>.</> : "You're lender-ready."}
            </h2>
            <p className="text-sm sm:text-base text-[#B6C3D9] mt-1 max-w-2xl">
              {report.headline}
            </p>
          </div>
          {/* Stat tiles — only on desktop; mobile keeps it clean */}
          <div className="hidden lg:grid grid-cols-2 gap-3 flex-shrink-0">
            <HeroStat
              label="Qualifying range"
              value={result ? `${fmt(result.low)} – ${fmt(result.high)}` : "—"}
            />
            <HeroStat label="Matched banks" value={matchedBanks || "—"} suffix={matchedBanks ? "lenders" : ""} />
          </div>
        </div>
        {/* Mobile stats — compact row */}
        <div className="lg:hidden mt-4 grid grid-cols-2 gap-2 relative">
          <HeroStat
            label="Qualifying range"
            value={result ? `${fmt(result.low)} – ${fmt(result.high)}` : "—"}
            compact
          />
          <HeroStat label="Matched banks" value={matchedBanks || "—"} suffix={matchedBanks ? "lenders" : ""} compact />
        </div>
      </div>

      <div
        className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 lg:p-8 shadow-sm lg:grid lg:grid-cols-[320px_1fr] lg:gap-10 space-y-6 lg:space-y-0"
        data-testid="pro-report"
      >
        {/* ======== LEFT COLUMN — sticky summary + CTAs on desktop ======== */}
        <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2 space-y-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[#3E9690]">
              <Crown className="w-4 h-4" />
              <p className="text-xs uppercase tracking-wider font-semibold">Premium Report Unlocked</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={sendProEmail}
              disabled={emailing || emailed}
              className="h-8 inline-flex items-center gap-1.5"
              data-testid="pro-email-btn"
            >
              {emailing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : emailed ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              {emailed ? "Sent" : "Email me"}
            </Button>
          </div>

          <div>
            <h3 className="text-2xl lg:text-[26px] font-bold text-[#2D3748] font-cabinet mb-1 leading-tight" data-testid="pro-headline">
              {report.headline}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">{report.summary}</p>
          </div>

          {/* Numbers block — grid on mobile, stacked on desktop for density */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500">Est. monthly (low)</p>
              <p className="font-bold text-[#2D3748]">{fmt(report.est_monthly_payment_low)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500">Est. monthly (high)</p>
              <p className="font-bold text-[#2D3748]">{fmt(report.est_monthly_payment_high)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500">Current TDSR</p>
              <p className="font-bold text-[#2D3748]">{report.tdsr_pct}%</p>
            </div>
          </div>

          {/* CTAs — prominent, always visible on desktop (sticky) */}
          <div className="pt-1 space-y-2">
            <div className="hidden lg:block rounded-xl bg-gradient-to-r from-[#52BFB8]/10 via-emerald-50 to-[#52BFB8]/10 border border-[#52BFB8]/20 p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest text-[#3E9690] font-bold mb-0.5">
                Ready for the real thing?
              </p>
              <p className="text-xs text-gray-500">
                Match your profile to every Bahamian bank in one submission.
              </p>
            </div>
            <Button
              onClick={onStartApp}
              className="btn-primary w-full h-12 inline-flex items-center justify-center gap-2 text-sm sm:text-base leading-none shadow-md shadow-[#52BFB8]/20"
              data-testid="full-app-btn"
            >
              <span className="truncate">See Where You Stand With The Banks</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
            </Button>
            <Button
              variant="outline"
              onClick={onBookBroker}
              className="w-full h-11 inline-flex items-center justify-center gap-2"
              data-testid="talk-broker-btn"
            >
              <Phone className="w-4 h-4" />
              Talk to a Broker Now
            </Button>
          </div>
        </aside>

      {/* ======== RIGHT COLUMN — details ======== */}
      <div className="space-y-6 min-w-0">
        {/* Not-yet: reasons + action plan */}
        {isNotYet && (
          <>
            {report.gap_amount > 0 && (
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 lg:p-5" data-testid="gap-block">
                <p className="text-xs uppercase tracking-wider text-rose-700 font-semibold mb-1">The gap</p>
                <p className="text-2xl lg:text-3xl font-bold text-rose-800">{fmt(report.gap_amount)}</p>
                <p className="text-xs text-rose-600 mt-1">
                  between your current qualifying range and this property.
                  {report.monthly_income_gap > 0 && ` Roughly ${fmt(report.monthly_income_gap)}/mo of additional gross income closes it.`}
                </p>
              </div>
            )}

            {report.reasons?.length > 0 && (
              <div data-testid="reasons-block">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">Why not yet</p>
                <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-2 lg:space-y-0">
                  {report.reasons.map((r, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700">
                      <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.action_plan?.length > 0 && (
              <div data-testid="action-plan-block">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">Your action plan</p>
                <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
                  {report.action_plan.map((a, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl p-4 h-full">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#52BFB8]/10 text-[#3E9690] flex items-center justify-center flex-shrink-0 text-sm font-bold">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#2D3748] text-sm">{a.step}</p>
                          <p className="text-xs text-gray-500 mt-1">{a.detail}</p>
                          <p className="text-xs text-[#3E9690] mt-1 font-medium">Impact: {a.impact}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.timeline_estimate && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                <Clock className="w-4 h-4 text-[#52BFB8]" />
                Estimated timeline to qualify: <strong>{report.timeline_estimate}</strong>
              </div>
            )}

            {report.similar_properties?.length > 0 && (
              <div data-testid="similar-properties-block">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">
                  Homes within reach right now
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {report.similar_properties.map((p, i) => {
                    const Card = p.href ? "a" : "div";
                    return (
                      <Card
                        key={p.id || i}
                        {...(p.href ? { href: p.href, target: "_blank", rel: "noreferrer" } : {})}
                        className="rounded-xl overflow-hidden border border-gray-100 bg-white hover:shadow-md hover:border-[#52BFB8]/40 transition group block"
                        data-testid={`similar-property-${i}`}
                      >
                        <div className="relative w-full aspect-[4/3] bg-gray-100 overflow-hidden">
                          <PropertyThumb
                            src={p.image}
                            alt={p.title}
                            className="group-hover:scale-105 transition duration-300"
                            testId={`similar-img-${i}`}
                          />
                          <div className="absolute top-2 left-2 bg-white/95 backdrop-blur rounded-full px-2 py-0.5 text-xs font-bold text-[#2D3748] shadow-sm">
                            {fmt(p.price)}
                          </div>
                          {p.source === "keysbahamas.com" && (
                            <div className="absolute top-2 right-2 bg-black/60 text-white rounded-full px-2 py-0.5 text-[10px] font-medium">
                              Live
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-semibold text-sm text-[#2D3748] line-clamp-1">{p.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.location}</p>
                          {(p.bedrooms || p.bathrooms) && (
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              {p.bedrooms && <span>{p.bedrooms} bd</span>}
                              {p.bedrooms && p.bathrooms && <span>·</span>}
                              {p.bathrooms && <span>{p.bathrooms} ba</span>}
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2 italic">
                  Live listings from keysbahamas.com. Click any card to view the full details.
                </p>
              </div>
            )}
          </>
        )}

        {/* Strong/Possible: lender playbook */}
        {!isNotYet && report.lender_playbook?.length > 0 && (
          <div data-testid="lender-playbook-block">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">Lender playbook</p>
            <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
              {report.lender_playbook.map((l, i) => (
                <div key={i} className="flex items-start gap-3 border border-gray-100 rounded-xl p-3">
                  <Building2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-semibold text-sm text-[#2D3748] truncate">{l.bank}</p>
                      <p className="text-xs font-bold text-[#3E9690]">{l.rate_est}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{l.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next steps — always shown */}
        {report.next_steps?.length > 0 && (
          <div data-testid="next-steps-block">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">Next steps</p>
            <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-2 lg:space-y-0">
              {report.next_steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <Target className="w-4 h-4 text-[#52BFB8] flex-shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mobile-only CTA fallback — on desktop these live in the sticky left column */}
        <div className="lg:hidden pt-2 space-y-2">
          <Button
            onClick={onStartApp}
            className="btn-primary w-full h-12 inline-flex items-center justify-center gap-2 text-sm sm:text-base leading-none"
          >
            <span className="truncate">See Where You Stand With The Banks</span>
            <ArrowRight className="w-4 h-4 flex-shrink-0" />
          </Button>
          <Button
            variant="outline"
            onClick={onBookBroker}
            className="w-full h-12 inline-flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Talk to a Broker Now
          </Button>
        </div>
      </div>
    </div>

      {/* ========= STICKY MOBILE CTA DOCK ========= */}
      <div
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-6px_18px_rgba(15,23,36,0.08)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        data-testid="pro-sticky-cta"
      >
        <Button
          onClick={onStartApp}
          className="btn-primary w-full h-12 inline-flex items-center justify-center gap-2 text-sm font-semibold"
        >
          <span className="truncate">See Where You Stand With The Banks</span>
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </Button>
      </div>
    </>
  );
}

/* ====== Hero banner stat tile ====== */
function HeroStat({ label, value, suffix, compact }) {
  return (
    <div
      className={`relative rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm ${
        compact ? "px-3 py-2" : "px-4 py-3 min-w-[160px]"
      }`}
    >
      <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
      <div className={`font-bold font-cabinet text-white ${compact ? "text-sm" : "text-lg"}`}>
        {value}{suffix ? <span className="text-white/60 text-xs font-medium ml-1">{suffix}</span> : null}
      </div>
    </div>
  );
}
